import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Spinner } from '@/components/ui/spinner'
import type { UserRole } from '@/types/api'
import { useAuth } from './auth-context'

/**
 * Client-side route guards are for navigation UX only — every protected
 * endpoint enforces its own auth/ownership checks server-side regardless of
 * what the client shows.
 */
export function RequireAuth({
  roles,
  requireVerified = true,
  children,
}: {
  roles?: UserRole[]
  /** Set to false only on the verify-otp page itself — everywhere else, an unverified account can't get past this gate. */
  requireVerified?: boolean
  children: ReactNode
}) {
  const { status, user } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <Spinner />
  }

  if (status === 'unauthenticated' || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />
  }

  if (requireVerified && !user.isVerified) {
    return <Navigate to="/verify-otp" replace />
  }

  return <>{children}</>
}
